import { useState } from 'react';
import { getFarmerProfileHistory, type FarmerProfileHistoryEntry } from '../../api/ekyc';
import { showToast } from '../base/Toast';

const FIELD_LABEL: Record<string, string> = {
  fullName: 'Họ tên',
  cccd: 'Số CCCD',
  phone: 'Số điện thoại',
  address: 'Địa chỉ',
  province: 'Tỉnh/thành',
  district: 'Huyện',
  ward: 'Xã',
  areaHa: 'Diện tích (ha)',
  householdMembers: 'Số thành viên lao động',
  mainCrop: 'Cây trồng chính',
  notes: 'Ghi chú',
};

const ROLE_LABEL: Record<string, string> = {
  FARMER: 'Nông hộ',
  HTX_STAFF: 'Hợp tác xã',
  ADMIN: 'Quản trị viên',
  OPS: 'Vận hành',
};

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '(trống)';
  return String(v);
}

/**
 * "Làm theo thay đổi lịch sử là tốt nhất" — a read-only view of
 * FarmerProfileHistoryEntry (GET /farmers/:id/profile-history), reused by
 * both the HTX-side farmer detail (DetailOverviewTab) and the farmer's own
 * profile screen so both sides can see exactly what changed, when and by
 * whom, instead of only ever knowing the current value.
 */
export function ProfileHistoryPanel({ farmerId }: { farmerId: string }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<FarmerProfileHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);

  async function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (entries !== null) return;
    setLoading(true);
    try {
      const rows = await getFarmerProfileHistory(farmerId);
      setEntries(rows);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Không tải được lịch sử thay đổi', 'error');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <button type="button" onClick={toggle} className="text-xs font-medium text-agrida-700 hover:underline flex items-center gap-1">
        <i className={`ri-${open ? 'arrow-up-s' : 'history'}-line`} />
        Lịch sử thay đổi hồ sơ
      </button>

      {open && (
        <div className="mt-2 rounded-xl border border-slate-200 bg-white p-3 max-h-64 overflow-y-auto">
          {loading && <p className="text-xs text-slate-400">Đang tải...</p>}
          {!loading && entries?.length === 0 && <p className="text-xs text-slate-400">Chưa có thay đổi nào được ghi nhận.</p>}
          {!loading && entries && entries.length > 0 && (
            <ul className="space-y-2.5">
              {entries.map((e) => (
                <li key={e._id} className="text-xs border-b border-slate-50 last:border-0 pb-2 last:pb-0">
                  <p className="text-slate-400">
                    {e.createdAt ? new Date(e.createdAt).toLocaleString('vi-VN') : ''} · {ROLE_LABEL[e.changedByRole] || e.changedByRole}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {Object.entries(e.changes).map(([field, { from, to }]) => (
                      <li key={field} className="text-slate-700">
                        <span className="font-medium">{FIELD_LABEL[field] || field}:</span> {formatValue(from)} → {formatValue(to)}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
